"use client"

// Live React rendering of the Aient lockup (`data-component="brand-wordmark"`).
// Static raster/SVG copies for emails, OG cards, slide decks, README embeds, etc.
// live in `media/brand/` and are built by `scripts/build-brand-wordmark.mjs` from
// the same `<AientLogo>` burst path + Instrument Serif Regular. If you change the
// layout or proportions here, rerun that script so the static artifacts match.

import { cva, type VariantProps } from "class-variance-authority"
import { AientLogo } from "@/components/icons/aient-logo"
import { cn } from "@/lib/utils"

const wordmarkVariants = cva("group inline-flex items-center", {
  variants: {
    tone: {
      dark: "text-brand-ink",
      /** Wordmark on void / ink sections (footer) — not tied to `--brand-white` (panel token). */
      light: "text-white",
      /** Theme-aware (e.g. auth / app surfaces using `text-foreground`). */
      foreground: "text-foreground",
    },
    size: {
      sm: "gap-2",
      md: "gap-2.5",
      lg: "gap-3",
    },
  },
  defaultVariants: {
    tone: "dark",
    size: "md",
  },
})

const wordmarkTextVariants = cva("font-brand-display font-normal tracking-[-0.01em]", {
  variants: {
    size: {
      sm: "text-[18px]",
      md: "text-[20px]",
      lg: "text-[22px]",
    },
  },
  defaultVariants: {
    size: "md",
  },
})

const logoSizes = {
  sm: 20,
  md: 34,
  lg: 24,
} as const

type BrandWordmarkProps = VariantProps<typeof wordmarkVariants> & {
  className?: string
  logoClassName?: string
  logoContainerClassName?: string
  textClassName?: string
  /** For Playwright / analytics; applied to the root element. */
  dataTestId?: string
}

export function BrandWordmark({
  className,
  logoClassName,
  logoContainerClassName,
  textClassName,
  size,
  tone,
  dataTestId,
}: BrandWordmarkProps) {
  const resolvedSize = size ?? "md"
  const resolvedTone = tone ?? "dark"

  const logoFill =
    resolvedTone === "light"
      ? "#ffffff"
      : resolvedTone === "dark"
        ? "var(--brand-ink)"
        : undefined

  return (
    <span
      className={cn(wordmarkVariants({ tone, size }), className)}
      data-component="brand-wordmark"
      data-testid={dataTestId}
    >
      <span className={cn("inline-flex shrink-0", logoContainerClassName)}>
        <AientLogo
          size={logoSizes[resolvedSize]}
          className={cn(
            "origin-center transition-transform duration-200 motion-safe:group-hover:scale-105",
            logoClassName,
          )}
          fill={logoFill}
          tight
        />
      </span>
      <span className={cn(wordmarkTextVariants({ size: resolvedSize }), textClassName)}>Aient</span>
    </span>
  )
}
